import { captureException } from '@/utils/captureException';

export type SseParsedEvent = {
  eventName: string | null;
  data: string;
  json: unknown | undefined;
};

export const ANSWER_RESTART_EVENT = 'answer_restart';

const ANSWER_RESTART_FALLBACK_MESSAGE = 'Генерация оборвалась, повторная попытка';

export abstract class BaseSseClient<Handlers> {
  private readonly _textDecoder = new TextDecoder('utf-8');

  protected extractAnswerRestartMessage(event: SseParsedEvent): string {
    return this.pickString(event.json, 'message') ?? ANSWER_RESTART_FALLBACK_MESSAGE;
  }

  protected pickString(obj: unknown, key: string): string | undefined {
    if (obj === null || typeof obj !== 'object') {
      return undefined;
    }

    const value = (obj as Record<string, unknown>)[key];

    return typeof value === 'string' ? value : undefined;
  }

  private _safeJsonParse(input: string): unknown | undefined {
    try {
      return JSON.parse(input);
    } catch {
      return undefined;
    }
  }

  private _parseEvent(rawEvent: string): SseParsedEvent {
    let eventName: string | null = null;
    const dataLines: string[] = [];
    const commentLines: string[] = [];

    for (const line of rawEvent.split(/\r?\n/)) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      } else if (line.startsWith(':')) {
        commentLines.push(line.slice(1).trim());
      }
    }

    let data = dataLines.join('\n');

    if (!data && commentLines.length > 0) {
      data = commentLines.join('\n');
      eventName = 'comment';
    }

    return { eventName, data, json: data ? this._safeJsonParse(data) : undefined };
  }

  private _processBuffer(buffer: string, handlers: Handlers): string {
    let boundaryIndex = buffer.indexOf('\n\n');

    while (boundaryIndex !== -1) {
      const rawEvent = buffer.slice(0, boundaryIndex);

      buffer = buffer.slice(boundaryIndex + 2);

      const parsed = this._parseEvent(rawEvent);

      this.handleEvent(parsed, handlers);

      boundaryIndex = buffer.indexOf('\n\n');
    }

    return buffer;
  }

  protected abstract handleEvent(event: SseParsedEvent, handlers: Handlers): void;

  async stream(params: { url: string; body: unknown; signal?: AbortSignal | null; handlers: Handlers }) {
    const { url, body, handlers, signal } = params;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(body),
      signal: signal ?? undefined,
      credentials: 'same-origin',
    });

    if (!response.ok || !response.body) {
      captureException(response, 'sse', {
        url,
        status: response.status,
        statusText: response.statusText,
      });
      throw new Error('BadResponse');
    }

    const reader = response.body.getReader();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();

      if (done) {
        break;
      }

      buffer += this._textDecoder.decode(value, { stream: true });
      buffer = this._processBuffer(buffer, handlers);
    }
  }
}
